// Beam Distributed Load (Truss) Types for ATCIVIL

export type BeamTrussType = 'distributed' | 'pressure' | 'center_pressure' | 'dual_pressure' | 'distributed_uniform' | 'distributed_variable' | 'concentrated_load' | 'distributed_type4' | 'twisted_load';

export interface BeamTruss {
  id: string;
  beamIds: string[];
  type: BeamTrussType;
  pressureValue: number; // For regular distributed and Type 4 loads
  leftPressureValue?: number; // For dual_pressure type
  rightPressureValue?: number; // For dual_pressure type
  
  // New distributed load properties
  dx?: number; // X-axis distributed load (uniform)
  dy?: number; // Y-axis distributed load (uniform)
  dz?: number; // Z-axis distributed load (uniform)
  
  // Variable distributed load properties (trapezoidal/triangular)
  dxLeft?: number; // X-axis left side value
  dyLeft?: number; // Y-axis left side value
  dzLeft?: number; // Z-axis left side value
  dxRight?: number; // X-axis right side value
  dyRight?: number; // Y-axis right side value
  dzRight?: number; // Z-axis right side value
  
  // Concentrated load properties
  fx?: number; // X-axis force (N)
  fy?: number; // Y-axis force (N)
  fz?: number; // Z-axis force (N)
  // Position-based properties for concentrated loads
  referenceNode?: 'node1' | 'node2'; // Which node to measure from (node1 = start, node2 = end)
  percentageFromNode?: number; // Percentage distance from the reference node (0-100%)
  
  // Type 4 curved/parabolic load properties
  type4PeakLoad?: number; // Peak load intensity at curve maximum
  type4PeakPosition?: number; // Peak position as percentage from start (0-100%)
  type4CurveType?: 'parabolic' | 'sinusoidal' | 'custom'; // Curve type for load distribution
  type4Direction?: 'dx' | 'dy' | 'dz'; // Load direction (dx, dy, dz)
  
  // Twisted load properties (torsional moment)
  torque?: number; // Torque value (N⋅m)
  torqueDirection?: 'clockwise' | 'anticlockwise'; // Direction of twist
  
  baseArrowLength: number; // Changed from baseArrowSize to baseArrowLength
  arrowCount: number;
  visible: boolean;
  label: string;
  color: string;
}

export interface BeamTrussArrow {
  position: {
    x: number;
    y: number;
    z: number;
  };
  size: number; // This represents length now
  direction: [number, number, number]; // Always [0, -1, 0] for downward
}

export interface BeamTrussState {
  beamTrusses: BeamTruss[];
  selectedBeamTrussId: string | null;
}

export interface BeamTrussVisualConfig {
  baseArrowLength: number; // Changed from baseArrowSize to baseArrowLength
  minArrowCount: number;
  maxArrowCount: number;
  spacingFactor: number; // Distance between arrows as factor of beam length
  defaultColor: string;
}

// Default configuration
export const DEFAULT_BEAM_TRUSS_CONFIG: BeamTrussVisualConfig = {
  baseArrowLength: 0.15, // Changed from baseArrowSize to baseArrowLength, increased default
  minArrowCount: 5,
  maxArrowCount: 20,
  spacingFactor: 0.05, // 5% of beam length
  defaultColor: '#000000' // Black color as requested
};
